import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useEffect } from "react";

import { LICENCE_READ_ONLY_EVENT } from "../api/client";
import type { LicenceStatus } from "../api/types";
import { designate, attentionSelector } from "../components/attention";
import { Callout } from "../components/Callout";
import { Motion, Presence } from "../components/presence";
import { Button, IconButton } from "../components/ui";
import { t } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { formatLicenceDay, LICENCE_QUERY } from "./licence";
import { LicenceDialog, useLicenceSummary } from "./LicenceFooter";
import { BANNER_TARGET, HOME_BANNER_TARGET, readOnlyInfo, readOnlyRefusal, useReadOnly } from "./readOnly";

/** Lecture seule d'une licence expirée (décision d'Alexis du 03/10/2026) : bandeau calme et durable, explication
 *  donnée une fois, puis désignation du bandeau à chaque action bloquée ; la fiche de la licence s'ouvre depuis l'un
 *  ou l'autre. Logique et règles : `readOnly.ts`. */

/** Texte du bandeau : « Licence expirée le 03/10/2026 : lecture seule jusqu'au 10/10/2026. » */
export function readOnlyText(expiredAt: string, until: string): string {
  return t("Licence expirée le {date} : lecture seule jusqu'au {until}.", { date: formatLicenceDay(expiredAt), until: formatLicenceDay(until) });
}

/** Action bloquée par la lecture seule : le bandeau (barre latérale, son rail, ou accueil) vient à l'écran et clignote,
 *  « Voir la licence » désigné. */
export function designateReadOnly(): void {
  designate(attentionSelector(useUi.getState().welcome ? HOME_BANNER_TARGET : BANNER_TARGET));
}

/** Bandeau de la lecture seule. `home` : sous le titre de l'accueil ; sinon dans la barre latérale (dépliée). */
export function ReadOnlyCallout({ home = false, className }: { home?: boolean; className?: string }) {
  const { status } = useLicenceSummary();
  const info = readOnlyInfo(status);
  const openPanel = useReadOnly((s) => s.openPanel);
  return (
    <Presence show={Boolean(info)}>
      {info && (
        <Callout tone="info" icon={KeyRound} className={className}>
          <span className="block">{readOnlyText(info.expiredAt, info.until)}</span>
          <span className="block pt-1">
            <Button variant="link" data-attention={home ? HOME_BANNER_TARGET : BANNER_TARGET} onClick={openPanel}>
              {t("Voir la licence")}
            </Button>
          </span>
        </Callout>
      )}
    </Presence>
  );
}

/** Barre latérale : le bandeau dépliée, une icône dans le rail (même cible désignée, même fiche). */
export function ReadOnlySidebar({ collapsed }: { collapsed: boolean }) {
  const { status } = useLicenceSummary();
  const info = readOnlyInfo(status);
  const openPanel = useReadOnly((s) => s.openPanel);
  return (
    <Presence show={Boolean(info)}>
      {info && (
        <Motion origin="fade" className="flex shrink-0 justify-center border-t border-line px-2.5 py-2">
          {collapsed ? (
            <IconButton
              size="sm"
              data-attention={BANNER_TARGET}
              onClick={openPanel}
              title={readOnlyText(info.expiredAt, info.until)}
              aria-label={readOnlyText(info.expiredAt, info.until)}
            >
              <KeyRound size={16} className="text-accent" />
            </IconButton>
          ) : (
            <ReadOnlyCallout className="w-full" />
          )}
        </Motion>
      )}
    </Presence>
  );
}

/** Refus `LICENCE_READ_ONLY` (événement du client de l'API, `detail` : le refus du serveur) : expliqué une fois par un
 *  avis qui ouvre la fiche de la licence, puis le bandeau est désigné à chaque refus ; l'état de la licence est relu
 *  s'il ne disait pas encore la lecture seule. */
export function handleReadOnlyRefusal(queryClient: QueryClient, detail: { read_only_until?: unknown } | undefined): void {
  const known = readOnlyInfo(queryClient.getQueryData<LicenceStatus>(LICENCE_QUERY));
  if (!known) void queryClient.invalidateQueries({ queryKey: LICENCE_QUERY });
  const until = known?.until ?? (typeof detail?.read_only_until === "string" ? detail.read_only_until : null);
  // Accueil ouvert : les avis y sont cachés (rendus sous lui) ; l'encadré d'erreur de la fenêtre explique le refus, et
  // l'explication reste à donner à la première action bloquée dans l'espace de travail (revue du 03/10/2026).
  const hidden = useUi.getState().welcome;
  if (readOnlyRefusal(Date.now(), !hidden) === "notice") {
    useUi.getState().notify({
      text: t("Licence expirée : lecture seule"),
      detail: until
        ? t("Generex reste consultable et ses téléchargements restent permis jusqu'au {until}. Renouvelez la licence pour reprendre le travail.", {
            until: formatLicenceDay(until),
          })
        : t("Generex reste consultable et ses téléchargements restent permis. Renouvelez la licence pour reprendre le travail."),
      tone: "info",
      action: { label: t("Voir la licence"), run: () => useReadOnly.getState().openPanel() },
    });
  }
  // Après le rendu du bandeau (état de la licence relu à l'instant, au premier refus).
  window.requestAnimationFrame(designateReadOnly);
}

/** Hôte (monté une fois, dans `App`) : écoute les refus de la lecture seule (`handleReadOnlyRefusal`) ; fiche de la
 *  licence ouverte depuis le bandeau ou l'avis (par l'accueil quand il est ouvert : il passe au-dessus des fenêtres de
 *  l'espace de travail). */
export function ReadOnlyHost() {
  const queryClient = useQueryClient();
  const { status } = useLicenceSummary();
  const open = useReadOnly((s) => s.panelOpen);
  const close = useReadOnly((s) => s.closePanel);
  // Accueil ouvert : la fiche s'ouvre dans son plan, au-dessus de lui (`WelcomeScreen`).
  const welcome = useUi((s) => s.welcome);
  useEffect(() => {
    const onRefused = (event: Event) =>
      handleReadOnlyRefusal(queryClient, (event as CustomEvent<{ read_only_until?: unknown } | undefined>).detail);
    window.addEventListener(LICENCE_READ_ONLY_EVENT, onRefused);
    return () => window.removeEventListener(LICENCE_READ_ONLY_EVENT, onRefused);
  }, [queryClient]);
  return <LicenceDialog open={open && !welcome && Boolean(status)} onClose={close} portal />;
}
